import { useState } from "react";
import axiosInstance from "../../../../common/infrastructure/helpers/axiosHelper";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import {
  getErrorToast,
  getSuccessToast,
} from "../../../../common/infrastructure/utilities/toastUtility";
import { IAutoPayStatusModel } from "../models/autoPayModel";

/**
 * Логиката на диалога "Автоматично плащане" + постоянния превключвател на
 * страницата с фактурите.
 *
 * Поведение на суича в диалога:
 *  - без запазена карта → включен по подразбиране (насърчаваме авто-плащането);
 *    "Продължи" минава през enroll Checkout (плаща + запазва картата)
 *  - със запазена карта → отразява текущото състояние; промяната се прилага
 *    при "Продължи" (SetAutoPay), а самото плащане минава през обикновен линк
 */
export const useInvoiceAutoPay = () => {
  const {
    data: autoPayStatus,
    isLoading: isStatusLoading,
    refetch: refetchStatus,
  } = useApiQuery<IAutoPayStatusModel>({
    queryKey: [endpointsMetadata.invoice?.getAutoPayStatus],
    endpoint: endpointsMetadata.invoice?.getAutoPayStatus,
    globalIsLoading: false,
  });

  const [payingInvoiceId, setPayingInvoiceId] = useState<number | null>(null);
  const [dialogSwitchOverride, setDialogSwitchOverride] = useState<boolean | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isTogglingAutoPay, setIsTogglingAutoPay] = useState(false);

  const hasCard = autoPayStatus?.hasCard ?? false;
  const isAutoPayEnabled = autoPayStatus?.enabled ?? false;

  // Изричен избор в диалога > текущо състояние (с карта) > включен по подразбиране
  const dialogSwitchValue = dialogSwitchOverride ?? (hasCard ? isAutoPayEnabled : true);

  const openPayDialog = (invoiceId: number) => {
    setDialogSwitchOverride(null);
    setPayingInvoiceId(invoiceId);
  };

  const closePayDialog = () => {
    if (!isProcessing) {
      setPayingInvoiceId(null);
    }
  };

  const setAutoPayRequest = (enabled: boolean) =>
    axiosInstance.post(`${endpointsMetadata.invoice!.setAutoPay}?enabled=${enabled}`);

  const continueToPayment = async () => {
    if (payingInvoiceId == null || isProcessing) {
      return;
    }

    setIsProcessing(true);
    try {
      // Със запазена карта суичът управлява директно състоянието на авто-плащането
      if (hasCard && dialogSwitchValue !== isAutoPayEnabled) {
        await setAutoPayRequest(dialogSwitchValue);
        await refetchStatus();
      }

      // Запазване на нова карта — само при включен суич и липса на карта
      const enableAutoPay = dialogSwitchValue && !hasCard;

      const response = await axiosInstance.post(
        `${endpointsMetadata.invoice!.generatePaymentLink}?invoiceId=${payingInvoiceId}&enableAutoPay=${enableAutoPay}`,
      );
      window.location.href = response.data;
    } catch {
      getErrorToast("Грешка при генерирането на линк за плащане.");
      setIsProcessing(false);
    }
  };

  const toggleAutoPay = async (enabled: boolean) => {
    if (isTogglingAutoPay) {
      return;
    }

    setIsTogglingAutoPay(true);
    try {
      await setAutoPayRequest(enabled);
      await refetchStatus();
      getSuccessToast(
        enabled
          ? "Автоматичното плащане е включено."
          : "Автоматичното плащане е изключено.",
      );
    } catch {
      getErrorToast("Грешка при промяната на автоматичното плащане.");
    } finally {
      setIsTogglingAutoPay(false);
    }
  };

  return {
    autoPayStatus,
    isStatusLoading,
    hasCard,
    isAutoPayEnabled,
    payingInvoiceId,
    isPayDialogOpen: payingInvoiceId != null,
    dialogSwitchValue,
    setDialogSwitch: setDialogSwitchOverride,
    openPayDialog,
    closePayDialog,
    continueToPayment,
    isProcessing,
    toggleAutoPay,
    isTogglingAutoPay,
  };
};
